import Vue from 'vue';

import customizerStore from './vuex/customizer/store';
import ecommerceStore from './vuex/ecommerce/store';

export default {
    state() {
        return {
            options: null,

            // Are we in the self-destruct mode.
            selfDestruct: false,

            // Global busy indicator.
            globalBusy: false,

            // Selected step.
            selectedStep: null,

            // Selected step parameters.
            selectedStepParameters: null,

            // Window width.
            windowWidth: window.innerWidth,

            // Renderer height.
            rendererHeight: null,

            // Is the exit confirmation dialog visible.
            showExitConfirmation: false,

            // Is the reset confirmation dialog visible.
            showResetConfirmation: false,

            // Is the done confirmation dialog visible.
            showDoneConfirmation: false,

            // Is the group confirmation dialog visible.
            showGroupConfirmation: false,

            // Is the individual confirmation dialog visible.
            showIndividualConfirmation: false,

            // Show TOS confirmation popup.
            showTosConfirmation: false,

            // Is this a group order
            isGroupOrder: false,

            // Show mobile group / individual selector
            showMobileOrderTypePrompt: true,

            // Design outside of bounds warning.
            designOutside: false,

            // Toast notification message.
            toastMessage: null,

            // Toast message type.
            toastMessageType: null,

            // Should image carousel swiping by disabled.
            lockSwipe: false,

            // Global event bus.
            eventBus: new Vue(),

            // Generic notification popup.
            showGenericNotify: null,

            // Generic confirmation popup.
            showGenericConfirm: null,

            // Translations.
            translations: null,
        };
    },

    mutations: {
        /**
         * Saves options.
         */
        SET_OPTIONS(state, options) {
            state.options = options;
        },

        /**
         * Self-destruct in ... 3... 2... 1...
         */
        SELFDESTUCT(state) {
            state.selfDestruct = true;
        },

        /**
         * Sets global busy status.
         */
        SET_GLOBAL_BUSY(state, v) {
            state.globalBusy = v;
        },

        /**
         * Update current step.
         */
        SET_SELECTED_STEP(state, v) {
            if (v && v.step) {
                state.selectedStep = v.step;
                state.selectedStepParameters = v.parameters;
            } else {
                state.selectedStep = v;
                state.selectedStepParameters = null;
            }
        },

        /**
         * Saves window width.
         */
        SET_WINDOW_WIDTH(state, v) {
            state.windowWidth = v;
        },

        /**
         * Saves renderer height.
         */
        SET_RENDERER_HEIGHT(state, v) {
            state.rendererHeight = v;
        },

        /**
         * Saves exit confirmation status.
         */
        SET_EXIT_CONFIRMATION(state, v) {
            state.showExitConfirmation = v;
        },

        /**
         * Saves reset confirmation status.
         */
        SET_RESET_CONFIRMATION(state, v) {
            state.showResetConfirmation = v;
        },

        /**
         * Saves done confirmation status.
         */
        SET_DONE_CONFIRMATION(state, v) {
            state.showDoneConfirmation = v;
        },

        /**
         * Saves group confirmation status.
         */
        SET_GROUP_CONFIRMATION(state, v) {
            state.showGroupConfirmation = v;
        },

        /**
         * Saves group confirmation status.
         */
        SET_INDIVIDUAL_CONFIRMATION(state, v) {
            state.showIndividualConfirmation = v;
        },

        /**
         * Saves TOS confirmation popup status.
         */
        SET_TOS_CONFIRMATION(state, v) {
            state.showTosConfirmation = v;
        },

        /**
         * Saves is group order status.
         */
        SET_IS_GROUP_ORDER(state, v) {
            state.isGroupOrder = v;
        },

        /**
         * Close mobile order type selection prompt
         */
        CLOSE_MOBILE_ORDER_TYPE_PROMPT(state) {
            state.showMobileOrderTypePrompt = false;
        },

        /**
         * Sets toast notification.
         */
        SET_TOAST(state, { message, type }) {
            state.toastMessage = message;
            state.toastMessageType = type;
        },

        /**
         * Save swipe lock state.
         */
        SET_LOCK_SWIPE(state, v) {
            state.lockSwipe = v;
        },

        /**
         * Saves notification popup parameters.
         */
        SET_GENERIC_NOTIFY(state, v) {
            state.showGenericNotify = v;
        },

        /**
         * Saves confirmation popup parameters.
         */
        SET_GENERIC_CONFIRM(state, v) {
            state.showGenericConfirm = v;
        },

        /**
         * Saves design warning.
         */
        SET_DESIGN_OUTSIDE(state, v) {
            state.designOutside = v;
        },

        /**
         * Save translations.
         */
        SET_TRANSLATIONS(state, v) {
            state.translations = v;
        },
    },

    actions: {
        /**
         * Saves options.
         */
        setOptions({ commit }, options) {
            commit('SET_OPTIONS', options);
        },

        /**
         * Save translations.
         */
        setTranslations({ commit }, v) {
            commit('SET_TRANSLATIONS', v);
        },

        /**
         * Select a new step.
         */
        selectStep({ state, commit }, v) {
            if (v == null && state.selectedStepParameters && state.selectedStepParameters.returnTo) {
                commit('SET_SELECTED_STEP', state.selectedStepParameters.returnTo);
            } else {
                commit('SET_SELECTED_STEP', v);
            }
        },

        /**
         * Cancels out from the widget.
         */
        globalCancel({ commit, state }) {
            if (state.options && state.options.onCancel) {
                state.options.onCancel();
            }

            commit('SELFDESTUCT');
        },

        /**
         * Cancels out from the widget.
         */
        globalSaveCancel({ commit, state }, result) {
            if (state.options && state.options.onSaveCancel) {
                state.options.onSaveCancel(result);
            }

            commit('SELFDESTUCT');
        },

        /**
         * Sends out a notification that the widget is busy.
         */
        globalBusy({ commit, state }, busy) {
            commit('SET_GLOBAL_BUSY', busy);

            if (state.options && state.options.onBusy) {
                state.options.onBusy(busy);
            }
        },

        /**
         * Sends out successful message and closes the widget.
         */
        globalDone({ state, commit }, result) {
            if (state.options && state.options.onDone) {
                state.options.onDone(result);
            }

            commit('SELFDESTUCT');
        },

        /**
         * Send analytics event.
         */
        analytics({ state }, event) {
            if (state.options.onAnalytics) {
                state.options.onAnalytics(event);
            }
        },

        /**
         * Saves window width.
         */
        setWindowWidth({ commit }, v) {
            commit('SET_WINDOW_WIDTH', v);
        },

        /**
         * Saves renderer height.
         */
        setRendererHeight({ commit }, v) {
            commit('SET_RENDERER_HEIGHT', v);
        },

        /**
         * Shows exit confirmation.
         */
        showExitConfirmation({ commit }, v) {
            commit('SET_EXIT_CONFIRMATION', v);
        },

        /**
         * Shows reset confirmation.
         */
        showResetConfirmation({ commit }, v) {
            commit('SET_RESET_CONFIRMATION', v);
        },

        /**
         * Shows done confirmation.
         */
        showDoneConfirmation({ commit }, v) {
            commit('SET_DONE_CONFIRMATION', v);
        },

        /**
         * Shows group confirmation order status.
         */
        showGroupConfirmation({ commit }, v) {
            commit('SET_GROUP_CONFIRMATION', v);
        },

        /**
         * Shows group confirmation order status.
         */
        showIndividualConfirmation({ commit }, v) {
            commit('SET_INDIVIDUAL_CONFIRMATION', v);
        },

        /**
         * Set the group order status.
         */
        setIsGroupOrder({ commit, dispatch }, v) {
            commit('SET_IS_GROUP_ORDER', v);
            if (v) {
                dispatch('restoreBottles');
            } else {
                dispatch('stashBottles');
            }
        },

        /**
         * Close mobile order type selection prompt
         */
        closeMobileOrderTypePrompt({ commit }, v) {
            commit('CLOSE_MOBILE_ORDER_TYPE_PROMPT', v);
        },

        /**
         * Shows toast notification.
         */
        toast({ commit }, v) {
            if (v == null) {
                commit('SET_TOAST', { message: null, type: null });
            } else {
                commit('SET_TOAST', v);
            }
        },

        /**
         * Locks image carousel swiping.
         */
        lockSwipe({ commit }, v) {
            commit('SET_LOCK_SWIPE', v);
        },

        /**
         * Sets height.
         */
        setHeight({ state }, v) {
            if (state.options.onSetHeight) {
                state.options.onSetHeight(v);
            }
        },

        /**
         * Dispatch event on a global event bus.
         */
        globalEvent({ state }, { event, data }) {
            state.eventBus.$emit(event, data);
        },

        /**
         * Validates editable views.
         */
        validateEditableViews({ dispatch }) {
            dispatch('globalEvent', {
                event: 'validateEditableViews',
            });
        },

        /**
         * Show notification popup.
         */
        showGenericNotify({ commit }, options) {
            commit('SET_GENERIC_NOTIFY', options);
        },

        /**
         * Show confirmation popup.
         */
        showGenericConfirm({ commit }, options) {
            commit('SET_GENERIC_CONFIRM', options);
        },

        /**
         * Sets design outside of bounds warning.
         */
        setDesignOutside({ commit }, v) {
            commit('SET_DESIGN_OUTSIDE', v);
        },

        showTosConfirmation({ commit }, v) {
            commit('SET_TOS_CONFIRMATION', v);
        },
    },

    modules: {
        ecommerce: ecommerceStore,
        customizer: customizerStore,
    },
};
